import React, { useEffect, useState } from 'react';

import useIsT2Experience from 'src/hooks/useIsT2Experience';
import { generateJobMatches, setIsUsingNewExperience, setJmrFilter } from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrJobTypes } from 'src/utils/jmrUtils';

import ResultsPageLegacy from '../resultsPage/resultsPageLegacy/resultsPageLegacy';

import ResultsPageNewExperience from './resultsPageNewExperience/resultsPageNewExperience';
import T2PlusBanner from './t2PlusBanner/t2PlusBanner';
import TldWarningModal from './tldWarningModal/tldWarningModal';

const ResultsPage = () => {
  const dispatch = useAppDispatch();
  const { allowedSites } = useAppSelector((state) => state.sites);
  const {
    backgroundFormData,
    isLoadingGetPlacementDetails,
    isUsingNewExperience,
    t2AdminToggle,
    jmrResults,
    jmrFilter,
    tldWarningModalAcknowledged,
  } = useAppSelector((state) => state.newJmrPage);
  const { featureFlags } = useAppSelector((state) => state.user);
  const isUsingT2Experience = useIsT2Experience();
  const [isLoading, setIsLoading] = useState(true);
  const [actualShiftLength, setActualShiftLength] = useState(0);
  const [isTldWarningModalOpen, setIsTldWarningModalOpen] = useState(false);
  const hasAvailableProcessPathJobs = jmrResults
    .filter((r) => r.jobType === JmrJobTypes.ProcessPath && (r.jobCapacity != null ? r.jobCapacity > 0 : true))
    .some((r) => !r.conflicts.length);

  // Check criteria to determine whether to use the new experience
  const determineUsingNewExperience = () =>
    featureFlags.JOB_MODIFICATIONS &&
    allowedSites.some((site) => site.site === backgroundFormData.siteLocation && site.jobModificationsEnabled);

  const loadResults = async () => {
    try {
      const isNewResultsPgExperienceEnabled = determineUsingNewExperience();
      await dispatch(generateJobMatches(isNewResultsPgExperienceEnabled ? 'v2' : 'v1'));
      dispatch(setIsUsingNewExperience(isNewResultsPgExperienceEnabled));
      setActualShiftLength(
        backgroundFormData.cannotWorkNormalShiftHrs
          ? +backgroundFormData.limitedHoursPerDay!
          : backgroundFormData.shiftLength,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Call generate job matches API matches
  useEffect(() => {
    // If coming to this page from update placments flow, ensure the existing placement's background information
    // and restrictions have been loaded before fetching job matches
    if (backgroundFormData.alias && !isLoadingGetPlacementDetails && allowedSites.length) {
      loadResults();
    }
    return () => {
      setIsLoading(true);
    };
  }, [backgroundFormData, isLoadingGetPlacementDetails, allowedSites, t2AdminToggle]);

  const handleTabChange = (jobType: JmrJobTypes) => {
    if (jobType === JmrJobTypes.TLD && !tldWarningModalAcknowledged && hasAvailableProcessPathJobs) {
      setIsTldWarningModalOpen(true);
    } else {
      dispatch(setJmrFilter({ ...jmrFilter, jobType }));
    }
  };
  return (
    <>
      {isUsingT2Experience && <T2PlusBanner />}

      {isUsingNewExperience || isUsingT2Experience ? (
        <ResultsPageNewExperience
          isLoading={isLoading}
          actualShiftLength={actualShiftLength}
          handleTabChange={handleTabChange}
        />
      ) : (
        <ResultsPageLegacy isLoading={isLoading} handleTabChange={handleTabChange} />
      )}

      <TldWarningModal isOpen={isTldWarningModalOpen} closeModal={() => setIsTldWarningModalOpen(false)} />
    </>
  );
};

export default ResultsPage;
